import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface MobileViewContextType {
  isMobileSimulation: boolean;
  toggleMobileView: () => void;
  isRealMobile: boolean;
  isInsidePreviewIframe: boolean;
  exitMobilePreview: () => void;
}

const MobileViewContext = createContext<MobileViewContextType | undefined>(undefined);

const STORAGE_KEY = 'mobile-view-simulation';
const MOBILE_BREAKPOINT = 1024; // lg breakpoint

export function MobileViewProvider({ children }: { children: ReactNode }) {
  const [isMobileSimulation, setIsMobileSimulation] = useState(false);
  const [isRealMobile, setIsRealMobile] = useState(false);
  const [isInsidePreviewIframe, setIsInsidePreviewIframe] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);

  // Check if we're inside the mobile preview iframe
  useEffect(() => {
    // Check for mobilePreview query param
    const urlParams = new URLSearchParams(window.location.search);
    const hasMobilePreviewParam = urlParams.get('mobilePreview') === 'true';

    // Also check if we're in an iframe (window.parent !== window)
    const isInIframe = window.parent !== window;

    setIsInsidePreviewIframe(hasMobilePreviewParam && isInIframe);
  }, []);

  // Check if we're on a real mobile device
  useEffect(() => {
    const checkViewport = () => {
      // If inside preview iframe, don't set as real mobile (we want mobile styles but it's simulated)
      if (isInsidePreviewIframe) {
        setIsRealMobile(false);
        return;
      }
      setIsRealMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    checkViewport();
    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, [isInsidePreviewIframe]);

  // Load saved state from localStorage after hydration
  useEffect(() => {
    setIsHydrated(true);
    // Don't load from storage if we're inside the iframe
    if (isInsidePreviewIframe) return;

    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'true' && window.innerWidth >= MOBILE_BREAKPOINT) {
      setIsMobileSimulation(true);
    }
  }, [isInsidePreviewIframe]);

  // Save state to localStorage
  useEffect(() => {
    // Don't save if we're inside the iframe
    if (isHydrated && !isInsidePreviewIframe) {
      localStorage.setItem(STORAGE_KEY, String(isMobileSimulation));
    }
  }, [isMobileSimulation, isHydrated, isInsidePreviewIframe]);

  // Auto-disable simulation if window becomes real mobile
  useEffect(() => {
    if (isRealMobile && isMobileSimulation && !isInsidePreviewIframe) {
      setIsMobileSimulation(false);
    }
  }, [isRealMobile, isMobileSimulation, isInsidePreviewIframe]);

  const toggleMobileView = () => {
    if (!isRealMobile && !isInsidePreviewIframe) {
      setIsMobileSimulation((prev) => !prev);
    }
  };

  // Function to exit mobile preview (sends message to parent window)
  const exitMobilePreview = () => {
    if (isInsidePreviewIframe && window.parent !== window) {
      window.parent.postMessage('exitMobilePreview', window.location.origin);
    }
  };

  // Notify parent window of navigation changes inside iframe
  useEffect(() => {
    if (isInsidePreviewIframe && window.parent !== window) {
      // Strip the mobilePreview param for the parent URL
      const cleanPath = window.location.pathname;
      window.parent.postMessage({ type: 'navigation', path: cleanPath }, window.location.origin);
    }
  }, [isInsidePreviewIframe]);

  return (
    <MobileViewContext.Provider value={{
      isMobileSimulation,
      toggleMobileView,
      isRealMobile,
      isInsidePreviewIframe,
      exitMobilePreview
    }}>
      {children}
    </MobileViewContext.Provider>
  );
}

export function useMobileView(): MobileViewContextType {
  const context = useContext(MobileViewContext);
  if (context === undefined) {
    throw new Error('useMobileView must be used within a MobileViewProvider');
  }
  return context;
}
